import React, { useEffect, useState } from 'react';
import { sendAuthenticatedRequest } from '../utils/api';
import './DeleteAccountModal.css';

// The right to erasure, served in the app rather than by somebody running SQL — GDPR Art. 17, and
// Apple requires an in-app route to delete an account for any app that offers one. The phone has
// had this since 2026-09-20 (UserDetails.js); the web had no way to do it at all.
//
// WHY ONE DIALOG HERE WHERE THE PHONE USES TWO. The phone chains two alerts, and its note says why:
// "the first is the question, the second is the consequence, which is the one people actually
// read". That holds, and the consequence text below is the phone's, word for word. But two chained
// alerts are a phone pattern born of a phone constraint — an iOS Alert cannot take text input.
//
// A browser can, so this asks for the username instead. It is strictly stronger than a second tap:
// two adjacent buttons can be cleared by muscle memory and a trackpad, and typing your own name
// cannot. One dialog, one deliberate act, and the destructive button stays inert until the name
// matches exactly.
const DeleteAccountModal = ({ isOpen, username, onClose, onDeleted, addNotification }) => {
  const [typed, setTyped] = useState('');
  const [working, setWorking] = useState(false);

  // Cleared on every open, so a half-typed name from a dialog somebody backed out of is never
  // sitting there ready to arm the button.
  useEffect(() => { if (isOpen) { setTyped(''); setWorking(false); } }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !working) onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, working]);

  if (!isOpen) return null;

  // Exact, including case: this is the one place in the app where being approximately right should
  // not be good enough.
  const matches = typed === username;

  const handleDelete = async () => {
    if (!matches || working) return;
    setWorking(true);
    try {
      await sendAuthenticatedRequest('/user/me', 'DELETE');
      // The account is gone, so there is nothing left to be signed in to.
      onDeleted();
    } catch (e) {
      setWorking(false);
      addNotification?.(
        e?.status === 404
          ? 'That account no longer exists.'
          : 'Could not delete the account. Please try again, or contact support if it keeps failing.',
        'red'
      );
    }
  };

  return (
    <div className="danger-overlay" onClick={() => !working && onClose()} role="presentation">
      <div
        className="danger-content"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-account-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="danger-title" id="delete-account-title">Delete your account?</h2>

        {/* The phone's words. "Are you sure?" tells nobody anything they did not already know; this
            names what goes. */}
        <p className="danger-body">
          This removes your account and everything in it, and it cannot be undone. Deleting{' '}
          <strong>{username}</strong> erases your parking history, your messages, the ratings you
          have given and received, and any spot you are currently sharing.
        </p>

        <label className="danger-field">
          <span className="field-label">Type <strong>{username}</strong> to confirm</span>
          <input
            className="control"
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck="false"
            disabled={working}
            aria-label={`Type ${username} to confirm`}
          />
        </label>

        <div className="btn-row">
          <button type="button" className="btn btn--secondary" onClick={onClose} disabled={working}>
            Keep my account
          </button>
          <button
            type="button"
            className="btn btn--danger"
            onClick={handleDelete}
            disabled={!matches || working}
          >
            {working ? 'Deleting…' : 'Delete for ever'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
